import { useEffect, useState } from "react";
import { Button, Card, Form, PageHeader, message, Row, Col, Space} from "antd";
import { useHistory, useParams } from "react-router-dom";
import LayoutCss from "layout/layout.module.scss";
import { Tooltip } from "common/components/tooltip/tooltip";
import RoleAndPermission from "redux/models/roleAndPermission";
import { setFieldErrorsFromServer } from "utilities/generalUtility";
import { RolePermissionsForm } from "./rolePermissionsForm";
import { groupPermissionsByGroupName } from "./groupPermissionsByGroupName";

const ROLES_PERMISSION_LISTING_ROUTE = "/configurations/roles-permission";

export default function CreateRolesPermission() {
  const { id: editId } = useParams();
  const history = useHistory();
  const [form] = Form.useForm();

  const [groups, setGroups] = useState([]);
  const [defaultSelectedIds, setDefaultSelectedIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedCount, setSelectedCount] = useState(0);
  const [hasDependencyWarning, setHasDependencyWarning] = useState(false);

  const goToListing = () => history.push(ROLES_PERMISSION_LISTING_ROUTE);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const [permissions, role] = await Promise.all([
        RoleAndPermission.getAllPermissions(),
        editId ? RoleAndPermission.getRoleById(editId) : Promise.resolve(null),
      ]);
      setGroups(groupPermissionsByGroupName(permissions));
      if (role) {
        setDefaultSelectedIds(role.permissions.map((pr) => pr.id));
        form.setFieldsValue({ name: role.displayName });
      }
      setLoading(false);
    })();
  }, [editId, form]);

  const handleFinish = async ({ name, permissions }) => {
    const data = { name, permissions };
    setIsSaving(true);
    try {
      if (editId) {
        await RoleAndPermission.updateRole(editId, data);
        message.success("Role updated successfully");
      } else {
        await RoleAndPermission.createRole(data);
        message.success("Role created successfully");
      }
      goToListing();
    } catch (error) {
      setFieldErrorsFromServer(error, form, data);
    } finally {
      setIsSaving(false);
    }
  };

  const saveDisabled = selectedCount === 0 || hasDependencyWarning;
  const saveTooltip = hasDependencyWarning
    ? "Resolve the permission dependency warnings before saving"
    : "";

  return (
    <>
      <PageHeader
        ghost={false}
        className={LayoutCss.appPageHeader}
        onBack={history.goBack}
        title={!editId ? "Create Role" : "Update Role"}
        extra={
          <>
            <Tooltip title={saveTooltip}>
              <span>
                <Button
                  type="primary"
                  loading={isSaving}
                  disabled={saveDisabled}
                  onClick={form.submit}
                >
                  Save
                </Button>
              </span>
            </Tooltip>
          </>
        }
      />

      {!loading && (
        <RolePermissionsForm
          key={editId}
          form={form}
          groups={groups}
          defaultSelectedIds={defaultSelectedIds}
          onFinish={handleFinish}
          onSelectionChange={({
            selectedCount: count,
            hasDependencyWarning: warning,
          }) => {
            setSelectedCount(count);
            setHasDependencyWarning(warning);
          }}
        />
      )}
    </>
  );
}
